import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useAccounts } from '../../hooks/useAccounts';
import useHousehold from '../../hooks/useHousehold';
import { authFetch } from '../../lib/authFetch';

type ManagedAccountType = 'creditCard' | 'checking' | 'savings';
type ManagedAccountCategory = 'asset' | 'liability';

interface Props {
  accountType: ManagedAccountType;
  accountCategory: ManagedAccountCategory;
  title: string;
  helperText: string;
}

const AccountsManagementPage: React.FC<Props> = ({
  accountType,
  accountCategory,
  title,
  helperText,
}) => {
  const { auth } = useAuth();
  const { loading: householdLoading, isEmpty: householdEmpty } = useHousehold();
  const { accounts, loading, error, refetch } = useAccounts();
  const [newName, setNewName] = useState('');
  const [newOpeningBalance, setNewOpeningBalance] = useState('');
  const [newAccessScope, setNewAccessScope] = useState<'private' | 'household'>('private');
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editAccessScope, setEditAccessScope] = useState<'private' | 'household'>('private');
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const scopedAccounts = useMemo(
    () =>
      accounts
        .filter((account) => account.accountType === accountType)
        .sort((a, b) => {
          const archivedDiff = Number(Boolean(a.isArchived)) - Number(Boolean(b.isArchived));
          if (archivedDiff !== 0) return archivedDiff;
          return a.name.localeCompare(b.name);
        }),
    [accounts, accountType]
  );

  const visibleAccounts = useMemo(
    () => (showArchived ? scopedAccounts : scopedAccounts.filter((account) => !account.isArchived)),
    [scopedAccounts, showArchived]
  );

  const createLabel = title.replace(' Accounts', '');
  const isChildUser = auth.user?.role === 'child';
  const canShareWithHousehold = !isChildUser && !householdLoading && !householdEmpty;
  const sharingHelperText = isChildUser
    ? 'Only Primary/Partner can change sharing.'
    : householdEmpty
      ? 'If you need household sharing enabled, please contact support.'
      : householdLoading
        ? 'Loading household...'
        : null;

  const handleCreate = async () => {
    if (!auth.token) return;
    const trimmedName = newName.trim();
    const trimmedOpeningBalance = newOpeningBalance.trim();
    const parsedOpeningBalance = Number(trimmedOpeningBalance);
    if (!trimmedName) {
      setSubmitError('Name is required.');
      return;
    }
    if (!trimmedOpeningBalance) {
      setSubmitError('Current balance as of today is required.');
      return;
    }
    if (Number.isNaN(parsedOpeningBalance)) {
      setSubmitError('Current balance as of today must be a valid number.');
      return;
    }

    setSaving(true);
    setSubmitError(null);
    try {
      const res = await authFetch('/api/accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          openingBalance: parsedOpeningBalance,
          accountType,
          accountCategory,
          accessScope: canShareWithHousehold ? newAccessScope : 'private',
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || `Failed to create ${createLabel.toLowerCase()} account`);
      }

      setNewName('');
      setNewOpeningBalance('');
      setNewAccessScope('private');
      refetch();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : `Failed to create ${createLabel.toLowerCase()} account`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (
    accountId: string,
    accountName: string,
    accountAccessScope?: 'private' | 'household'
  ) => {
    setEditingAccountId(accountId);
    setEditName(accountName);
    setEditAccessScope(accountAccessScope === 'household' ? 'household' : 'private');
    setSubmitError(null);
  };

  const handleCancelEdit = () => {
    setEditingAccountId(null);
    setEditName('');
    setEditAccessScope('private');
    setSubmitError(null);
  };

  const handleSaveEdit = async (accountId: string) => {
    if (!auth.token) return;
    const trimmedName = editName.trim();
    if (!trimmedName) {
      setSubmitError('Name is required.');
      return;
    }

    setSaving(true);
    setSubmitError(null);
    try {
      const res = await authFetch(`/api/accounts/${accountId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          accessScope: !householdEmpty && editAccessScope === 'household' ? 'household' : 'private',
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || 'Failed to update account');
      }
      handleCancelEdit();
      refetch();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleArchive = async (accountId: string, nextArchivedState: boolean) => {
    if (!auth.token) return;

    setSaving(true);
    setSubmitError(null);
    try {
      const res = await authFetch(`/api/accounts/${accountId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({ isArchived: nextArchivedState }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || 'Failed to update archive status');
      }
      if (editingAccountId === accountId) {
        handleCancelEdit();
      }
      refetch();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to update archive status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          {`Create ${createLabel}`}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
          <TextField
            label="Current balance as of today"
            helperText="Snapshot amount used as today's baseline."
            size="small"
            type="number"
            fullWidth
            required
            value={newOpeningBalance}
            onChange={(event) => setNewOpeningBalance(event.target.value)}
          />
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !auth.token}
          >
            Create
          </Button>
        </Stack>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25, mt: 1.25 }}>
          <FormControl component="fieldset" disabled={isChildUser || saving} fullWidth>
            <FormLabel component="legend">Sharing</FormLabel>
            <RadioGroup
              value={newAccessScope}
              onChange={(event) =>
                setNewAccessScope(event.target.value as 'private' | 'household')
              }
            >
              <FormControlLabel value="private" control={<Radio />} label="Private (only me)" />
              <FormControlLabel
                value="household"
                control={<Radio />}
                label="Shared with household"
                disabled={!canShareWithHousehold}
              />
            </RadioGroup>
          </FormControl>
          {sharingHelperText && (
            <Typography variant="body2" color="text.secondary">
              {sharingHelperText}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 1.5 }}
        >
          <Typography fontWeight={600}>{`${title} List`}</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                onChange={(_, checked) => setShowArchived(checked)}
              />
            }
            label="Show archived"
          />
        </Stack>

        {loading ? (
          <Typography color="text.secondary">Loading accounts...</Typography>
        ) : error ? (
          <Typography color="error.main">{error}</Typography>
        ) : visibleAccounts.length === 0 ? (
          <Typography color="text.secondary">No accounts to show.</Typography>
        ) : (
          <Stack spacing={1}>
            {visibleAccounts.map((account) => {
              const isEditing = editingAccountId === account._id;
              const isArchived = Boolean(account.isArchived);
              return (
                <Box
                  key={account._id}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}
                >
                  {isEditing ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        fullWidth
                        label="Name"
                        value={editName}
                        onChange={(event) => setEditName(event.target.value)}
                      />
                      <FormControl component="fieldset" disabled={isChildUser || saving} fullWidth>
                        <FormLabel component="legend">Sharing</FormLabel>
                        <RadioGroup
                          value={editAccessScope}
                          onChange={(event) =>
                            setEditAccessScope(event.target.value as 'private' | 'household')
                          }
                        >
                          <FormControlLabel
                            value="private"
                            control={<Radio />}
                            label="Private (only me)"
                          />
                          <FormControlLabel
                            value="household"
                            control={<Radio />}
                            label="Shared with household"
                            disabled={!canShareWithHousehold}
                          />
                        </RadioGroup>
                      </FormControl>
                      {sharingHelperText && (
                        <Typography variant="body2" color="text.secondary">
                          {sharingHelperText}
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        onClick={() => handleSaveEdit(account._id)}
                        disabled={saving}
                      >
                        Save
                      </Button>
                      <Button variant="text" onClick={handleCancelEdit} disabled={saving}>
                        Cancel
                      </Button>
                    </Stack>
                  ) : (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                    >
                      <Box>
                        <Typography fontWeight={600}>{account.name}</Typography>
                        {isArchived && (
                          <Typography variant="caption" color="text.secondary">
                            Archived
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          onClick={() =>
                            handleStartEdit(account._id, account.name, account.accessScope)
                          }
                          disabled={saving}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color={isArchived ? 'primary' : 'warning'}
                          onClick={() => handleToggleArchive(account._id, !isArchived)}
                          disabled={saving}
                        >
                          {isArchived ? 'Unarchive' : 'Archive'}
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {submitError && (
          <Typography color="error.main" sx={{ mt: 1.5 }}>
            {submitError}
          </Typography>
        )}
      </Paper>
    </Stack>
  );
};

export default AccountsManagementPage;
